import { AxiosResponse } from "axios";
import { call, CallEffect } from "redux-saga/effects";

import { AxiosInterceptedResult } from "../../../api/interceptors";
import { SagaAction } from "../../../sagas/types";

import { hasErrorRecords } from "../../../parsers/resolve";

interface ICallbackFetchIdsAndResolveThem<T> {
  getEditableByRelativeTo: (accountId: string, organizationId: string) => Promise<AxiosResponse<string[]>>;
  resolve: (accountId: string, ids: string[]) => Promise<AxiosResponse<T[]>>;
}

type Lit = string | number | boolean | undefined | null | void | {};
const tuple = <T extends Lit[]>(...args: T) => args;
export type FetchResult<T = any> = [null, T] | [{ message: string }, null];
export type FetchIdsAndResolve<T = any> = FetchResult<[AxiosResponse<string[]>, AxiosResponse<T[]>]>;

interface IFetchIdsOpts {
  identityName: string;
  includeErrors: boolean;
}

export const fetchIdsAndResolveThem = <T extends {}>(
  callbacks: ICallbackFetchIdsAndResolveThem<T[]>,
  { identityName, includeErrors }: IFetchIdsOpts = {
    identityName: "",
    includeErrors: false,
  }
) => (accountId: string, organizationId: string) => {
  return function*(): SagaAction<CallEffect, FetchIdsAndResolve<T>> {
    const [idsErr, idsResp]: AxiosInterceptedResult<string[]> = yield call(
      callbacks.getEditableByRelativeTo,
      accountId,
      organizationId
    );
    const name = identityName ? `${identityName} ` : "";
    if (idsErr || !idsResp) {
      return tuple(
        {
          message: `Failed to fetch ${name}ids`,
        },
        null
      );
    } else {
      const [resolveErr, resolveResp]: AxiosInterceptedResult<T[]> = yield call(
        callbacks.resolve,
        accountId,
        idsResp.data
      );
      if (resolveErr || !resolveResp) {
        return tuple({ message: `Could not resolve ${name}data` }, null);
      } else if (hasErrorRecords(resolveResp.data) && !includeErrors) {
        return tuple({ message: `Failed because ${name}resolved data contained errors` }, null);
      } else {
        return tuple(null, tuple(idsResp, resolveResp));
      }
    }
  };
};
